import React, { useEffect, useState } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Wordmark, LogoMark } from '@/components/brand/Logo';
import { Menu, X, Phone, Mail } from 'lucide-react';

const NAV = [
  { label: 'Platform', href: '#platform' },
  { label: 'How It Works', href: '#how-it-works' },
  { label: 'Capabilities', href: '#capabilities' },
  { label: 'Sectors', href: '#sectors' },
  { label: 'Enterprise Security', href: '#enterprise' },
  { label: 'About', href: '#about' },
];

function scrollToHash(hash) {
  if (!hash) return;
  const id = hash.replace('#', '');
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

function PublicNav() {
  const nav = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleNavClick = (e, href) => {
    if (!href.startsWith('#')) return;
    e.preventDefault();
    setOpen(false);
    if (location.pathname !== '/') {
      nav('/');
      // Wait for navigation before scrolling.
      setTimeout(() => scrollToHash(href), 120);
    } else {
      scrollToHash(href);
    }
  };

  return (
    <header
      className={`sticky top-0 z-40 transition-shadow ${scrolled ? 'shadow-[0_1px_0_0_hsl(var(--border))]' : ''} bg-background/95 backdrop-blur border-b border-border`}
      data-testid="public-header"
    >
      <div className="max-w-[1440px] mx-auto px-4 lg:px-8 h-20 flex items-center justify-between gap-3">
        <NavLink to="/" data-testid="public-nav-brand" className="shrink-0 max-w-[220px] xl:max-w-none">
          <Wordmark />
        </NavLink>

        <nav className="hidden xl:flex items-center gap-5 text-[13px] whitespace-nowrap">
          {NAV.map(item => (
            <a
              key={item.label}
              href={item.href}
              onClick={(e) => handleNavClick(e, item.href)}
              className="text-muted-foreground hover:text-foreground transition-colors font-medium whitespace-nowrap"
              data-testid={`public-nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
            >
              {item.label}
            </a>
          ))}
          <NavLink
            to="/contact"
            className={({ isActive }) => `font-medium transition-colors whitespace-nowrap ${isActive ? 'text-primary' : 'text-muted-foreground hover:text-foreground'}`}
            data-testid="public-nav-contact"
          >
            Contact
          </NavLink>
        </nav>

        {/* Condensed nav for md-lg breakpoints */}
        <nav className="hidden md:flex xl:hidden items-center gap-5 text-sm whitespace-nowrap">
          <a href="#capabilities" onClick={(e) => handleNavClick(e, '#capabilities')} className="text-muted-foreground hover:text-foreground font-medium">Capabilities</a>
          <a href="#sectors" onClick={(e) => handleNavClick(e, '#sectors')} className="text-muted-foreground hover:text-foreground font-medium">Sectors</a>
          <a href="#about" onClick={(e) => handleNavClick(e, '#about')} className="text-muted-foreground hover:text-foreground font-medium">About</a>
          <NavLink to="/contact" className={({isActive}) => `font-medium whitespace-nowrap ${isActive ? 'text-primary' : 'text-muted-foreground hover:text-foreground'}`}>Contact</NavLink>
        </nav>

        <div className="hidden md:flex items-center gap-2 shrink-0">
          <Button variant="ghost" size="sm" onClick={() => nav('/login')} data-testid="public-nav-login">
            Secure Login
          </Button>
          <Button size="sm" onClick={() => nav('/request-demo')} data-testid="public-nav-demo" className="whitespace-nowrap">
            Request Demo
          </Button>
        </div>

        <button
          className="lg:hidden md:hidden inline-flex items-center justify-center rounded-md p-2 text-muted-foreground hover:text-foreground"
          onClick={() => setOpen(o => !o)}
          aria-label="Toggle navigation"
          data-testid="public-nav-toggle"
        >
          {open ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-border bg-background" data-testid="public-nav-mobile">
          <div className="px-4 py-4 space-y-1">
            {NAV.map(item => (
              <a
                key={item.label}
                href={item.href}
                onClick={(e) => handleNavClick(e, item.href)}
                className="block py-2 text-sm font-medium text-muted-foreground hover:text-foreground"
              >
                {item.label}
              </a>
            ))}
            <NavLink
              to="/contact"
              onClick={() => setOpen(false)}
              className="block py-2 text-sm font-medium text-muted-foreground hover:text-foreground"
            >
              Contact
            </NavLink>
            <div className="pt-3 border-t border-border">
              <div className="text-[10px] text-muted-foreground mb-2">
                A product by{' '}
                <a href="https://anytechventures.com/" target="_blank" rel="noreferrer" className="underline hover:text-primary">
                  Any Tech Ventures Pvt. Ltd.
                </a>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Button variant="outline" size="sm" onClick={() => { setOpen(false); nav('/login'); }}>
                  Secure Login
                </Button>
                <Button size="sm" onClick={() => { setOpen(false); nav('/request-demo'); }}>
                  Request Demo
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}

function PublicFooter() {
  const nav = useNavigate();
  return (
    <footer className="border-t border-border bg-secondary/40 mt-16" data-testid="public-footer">
      <div className="max-w-7xl mx-auto px-4 lg:px-8 py-12 grid grid-cols-2 md:grid-cols-5 gap-8">
        <div className="col-span-2">
          <div className="flex items-center gap-2">
            <LogoMark size={28} className="text-primary" />
            <div>
              <div className="font-display font-semibold text-[16px]">Aakash Drishti</div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Satellite Intelligence for Critical Infrastructure</div>
            </div>
          </div>
          <p className="mt-4 text-sm text-muted-foreground max-w-md">
            Aakash Drishti helps government organisations, public sector undertakings and infrastructure operators monitor critical assets using recurring satellite observations, geospatial intelligence and AI-assisted change detection.
          </p>
          <p className="mt-3 text-xs text-muted-foreground">
            A product by{' '}
            <a href="https://anytechventures.com/" target="_blank" rel="noreferrer" className="text-foreground underline underline-offset-2 decoration-dotted hover:text-primary">
              Any Tech Ventures Pvt. Ltd.
            </a>
          </p>
        </div>

        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground mb-3">Product</div>
          <ul className="space-y-2 text-sm">
            <li><a href="/#platform" className="hover:text-primary">Platform</a></li>
            <li><a href="/#capabilities" className="hover:text-primary">Capabilities</a></li>
            <li><a href="/#sectors" className="hover:text-primary">Sectors</a></li>
            <li><a href="/#enterprise" className="hover:text-primary">Enterprise Security</a></li>
            <li><button onClick={() => nav('/login')} className="hover:text-primary text-left">Secure Login</button></li>
          </ul>
        </div>

        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground mb-3">Company</div>
          <ul className="space-y-2 text-sm">
            <li>
              <a href="https://anytechventures.com/" target="_blank" rel="noreferrer" className="hover:text-primary">
                About Any Tech Ventures
              </a>
            </li>
            <li><NavLink to="/contact" className="hover:text-primary">Contact</NavLink></li>
            <li><NavLink to="/request-demo" className="hover:text-primary">Request demonstration</NavLink></li>
          </ul>
        </div>

        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground mb-3">Legal</div>
          <ul className="space-y-2 text-sm">
            <li><NavLink to="/privacy" className="hover:text-primary">Privacy Policy</NavLink></li>
            <li><NavLink to="/terms" className="hover:text-primary">Terms of Use</NavLink></li>
            <li><a href="/#disclaimer" className="hover:text-primary">Product Disclaimer</a></li>
          </ul>
        </div>
      </div>

      <div className="border-t border-border">
        <div className="max-w-7xl mx-auto px-4 lg:px-8 py-5 text-xs text-muted-foreground grid md:grid-cols-2 gap-2" id="disclaimer">
          <div>
            © {new Date().getFullYear()} Any Tech Ventures Pvt. Ltd. All rights reserved. Aakash Drishti is a product of Any Tech Ventures Pvt. Ltd.
          </div>
          <div className="md:text-right">
            <span className="inline-flex items-center gap-1"><Mail size={12} /> hello@anytechventures.com</span>
            <span className="mx-2">·</span>
            <span className="inline-flex items-center gap-1"><Phone size={12} /> +91-9870135066</span>
          </div>
          <div className="md:col-span-2 mt-1 text-[11px] max-w-3xl">
            <b>Product disclaimer:</b> Satellite observation frequency and detection capability depend on sensor availability, imagery quality, spatial resolution, weather conditions and the configured monitoring area. Aakash Drishti complements physical inspection, instrumentation and engineering analysis; it does not replace them. Demonstration data shown in the platform is simulated and does not represent actual events at the displayed locations.
          </div>
        </div>
      </div>
    </footer>
  );
}

export default function PublicLayout() {
  useEffect(() => { document.documentElement.classList.remove('dark'); }, []);
  return (
    <div className="min-h-screen flex flex-col bg-background text-foreground">
      <PublicNav />
      <main className="flex-1"><Outlet /></main>
      <PublicFooter />
    </div>
  );
}
